class AnnouncementBar extends HTMLElement {
  #index = 0;
  #timer = null;
  #hovering = false;
  #boundPrev = null;
  #boundNext = null;

  connectedCallback() {
    this.#index = Math.max(0, this.slides.findIndex((slide) => slide.getAttribute('aria-hidden') === 'false'));
    this.show(this.#index, false);

    this.addEventListener('mouseenter', this.#onMouseEnter);
    this.addEventListener('mouseleave', this.#onMouseLeave);
    document.addEventListener('visibilitychange', this.#onVisibilityChange);

    const previous = this.querySelector('.slideshow-control--previous');
    const next = this.querySelector('.slideshow-control--next');

    if (previous) {
      this.#boundPrev = (event) => {
        event.preventDefault();
        this.previous();
        this.restart();
      };
      previous.addEventListener('click', this.#boundPrev);
    }

    if (next) {
      this.#boundNext = (event) => {
        event.preventDefault();
        this.next();
        this.restart();
      };
      next.addEventListener('click', this.#boundNext);
    }

    this.play();
  }

  disconnectedCallback() {
    this.stop();
    this.removeEventListener('mouseenter', this.#onMouseEnter);
    this.removeEventListener('mouseleave', this.#onMouseLeave);
    document.removeEventListener('visibilitychange', this.#onVisibilityChange);

    const previous = this.querySelector('.slideshow-control--previous');
    const next = this.querySelector('.slideshow-control--next');
    if (previous && this.#boundPrev) previous.removeEventListener('click', this.#boundPrev);
    if (next && this.#boundNext) next.removeEventListener('click', this.#boundNext);
  }

  get slides() {
    return Array.from(this.querySelectorAll('.announcement-bar__slide'));
  }

  get delay() {
    const seconds = Number.parseInt(this.getAttribute('autoplay') || '4', 10);
    return (Number.isFinite(seconds) && seconds > 0 ? seconds : 4) * 1000;
  }

  show(index, animate = true) {
    const slides = this.slides;
    if (!slides.length) return;

    this.#index = ((index % slides.length) + slides.length) % slides.length;

    slides.forEach((slide, slideIndex) => {
      const active = slideIndex === this.#index;
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      slide.toggleAttribute('data-active', active);
    });

    if (animate && slides[this.#index]?.animate) {
      slides[this.#index].animate(
        [
          { opacity: 0, transform: 'translateX(28px)' },
          { opacity: 1, transform: 'translateX(0)' }
        ],
        { duration: 480, easing: 'cubic-bezier(.22,.61,.36,1)' }
      );
    }
  }

  next() {
    if (this.slides.length < 2) return;
    this.show(this.#index + 1);
  }

  previous() {
    if (this.slides.length < 2) return;
    this.show(this.#index - 1);
  }

  play() {
    if (!this.hasAttribute('autoplay') || this.slides.length < 2) return;
    this.stop();
    this.#timer = window.setInterval(() => {
      if (!this.#hovering && !document.hidden) this.next();
    }, this.delay);
  }

  stop() {
    if (this.#timer) window.clearInterval(this.#timer);
    this.#timer = null;
  }

  restart() {
    this.play();
  }

  #onMouseEnter = () => {
    this.#hovering = true;
  };

  #onMouseLeave = () => {
    this.#hovering = false;
  };

  #onVisibilityChange = () => {
    if (!document.hidden) this.restart();
  };
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
